// utils/usePersistentState.js
import { useState, useEffect } from "react";

/**
 * HIPAA-COMPLIANT UI PREFERENCES STORAGE
 *
 * This module is explicitly designed for UI preferences ONLY.
 * It uses a whitelist approach - only pre-approved keys can be stored.
 *
 * DO NOT MODIFY THIS FILE without security review.
 */

// Whitelist of allowed preference keys
const ALLOWED_PREFERENCES = {
  // Theme and appearance
  "ui-theme": { type: "string", values: ["light", "dark", "auto"] },
  "ui-language": { type: "string" },
  "ui-date-format": { type: "string" },
  "ui-time-format": { type: "string" },

  // Layout preferences
  "ui-sidebar-open": { type: "boolean" },
  "ui-sidebar-width": { type: "number", min: 200, max: 400 },
  "ui-compact-view": { type: "boolean" },

  // Table preferences
  "ui-table-page-size": { type: "number", values: [10, 25, 50, 100] },
  "ui-table-density": {
    type: "string",
    values: ["compact", "normal", "comfortable"],
  },

  // Dashboard preferences
  "ui-dashboard-layout": { type: "string" },
  "ui-widget-order": { type: "array" },

  // Notification preferences (UI only - actual settings are server-side)
  "ui-notification-position": {
    type: "string",
    values: ["top-right", "top-left", "bottom-right", "bottom-left"],
  },
};

/**
 * Validates that a key is allowed and the value matches expected type/format
 */
const validatePreference = (key, value) => {
  const config = ALLOWED_PREFERENCES[key];
  if (!config) {
    throw new Error(
      `Preference key "${key}" is not allowed. Only UI preferences can be stored locally.`
    );
  }

  // Type checking
  if (config.type === "boolean" && typeof value !== "boolean") {
    throw new Error(`Preference "${key}" must be a boolean`);
  }
  if (config.type === "string" && typeof value !== "string") {
    throw new Error(`Preference "${key}" must be a string`);
  }
  if (config.type === "number" && typeof value !== "number") {
    throw new Error(`Preference "${key}" must be a number`);
  }
  if (config.type === "array" && !Array.isArray(value)) {
    throw new Error(`Preference "${key}" must be an array`);
  }

  // Value validation
  if (config.values && !config.values.includes(value)) {
    throw new Error(
      `Preference "${key}" must be one of: ${config.values.join(", ")}`
    );
  }
  if (config.min !== undefined && value < config.min) {
    throw new Error(`Preference "${key}" must be at least ${config.min}`);
  }
  if (config.max !== undefined && value > config.max) {
    throw new Error(`Preference "${key}" must be at most ${config.max}`);
  }

  return true;
};

/**
 * Hook for UI preferences only - enforces whitelist
 */
export const useUIPreference = (key, defaultValue) => {
  // Validate on initialization
  if (!ALLOWED_PREFERENCES[key]) {
    throw new Error(
      `❌ Security Error: "${key}" is not an allowed UI preference. ` +
        `Allowed keys: ${Object.keys(ALLOWED_PREFERENCES).join(", ")}`
    );
  }

  const [value, setValue] = useState(() => {
    try {
      const stored = localStorage.getItem(`pref_${key}`);
      if (stored) {
        const parsed = JSON.parse(stored);
        validatePreference(key, parsed);
        return parsed;
      }
      return defaultValue;
    } catch (err) {
      console.warn(`Failed to load preference "${key}":`, err.message);
      return defaultValue;
    }
  });

  const setPreference = (newValue) => {
    try {
      validatePreference(key, newValue);
      setValue(newValue);
    } catch (err) {
      console.error(`Failed to set preference "${key}":`, err.message);
      throw err;
    }
  };

  useEffect(() => {
    try {
      localStorage.setItem(`pref_${key}`, JSON.stringify(value));
    } catch (err) {
      console.warn(`Failed to save preference "${key}":`, err.message);
    }
  }, [key, value]);

  return [value, setPreference];
};

/**
 * Get all UI preferences (for syncing with server)
 */
export const getAllUIPreferences = () => {
  const preferences = {};
  Object.keys(ALLOWED_PREFERENCES).forEach((key) => {
    try {
      const stored = localStorage.getItem(`pref_${key}`);
      if (stored) {
        preferences[key] = JSON.parse(stored);
      }
    } catch (err) {
      // Skip invalid entries
    }
  });
  return preferences;
};

/**
 * Clear all UI preferences
 */
export const clearAllUIPreferences = () => {
  Object.keys(ALLOWED_PREFERENCES).forEach((key) => {
    localStorage.removeItem(`pref_${key}`);
  });
};

/**
 * Check if a key is allowed (for migration purposes)
 */
export const isAllowedPreference = (key) => {
  return ALLOWED_PREFERENCES.hasOwnProperty(key);
};

// ✅ HIPAA COMPLIANCE UTILITIES

/**
 * Migrates old UI preferences to HIPAA-compliant format
 */
export const migrateOldUIPreferences = () => {
  console.log("🔄 Migrating old UI preferences to HIPAA-compliant format...");

  // Common old keys that might exist
  const oldToNewMap = {
    theme: "ui-theme",
    language: "ui-language",
    sidebar: "ui-sidebar-open",
    sidebarOpen: "ui-sidebar-open",
    pageSize: "ui-table-page-size",
    tablePageSize: "ui-table-page-size",
    density: "ui-table-density",
    tableDensity: "ui-table-density",
    dashboardLayout: "ui-dashboard-layout",
    notificationPosition: "ui-notification-position",
  };

  let migrated = 0;

  Object.entries(oldToNewMap).forEach(([oldKey, newKey]) => {
    const oldValue = localStorage.getItem(oldKey);
    if (oldValue && !localStorage.getItem(`pref_${newKey}`)) {
      try {
        const parsed = JSON.parse(oldValue);
        if (ALLOWED_PREFERENCES[newKey]) {
          validatePreference(newKey, parsed);
          localStorage.setItem(`pref_${newKey}`, oldValue);
          localStorage.removeItem(oldKey);
          migrated++;
          console.log(`✅ Migrated ${oldKey} → pref_${newKey}`);
        }
      } catch (error) {
        console.warn(`⚠️ Failed to migrate ${oldKey}:`, error.message);
        localStorage.removeItem(oldKey); // Remove invalid data
      }
    } else if (oldValue) {
      // Old key exists but new key already exists, just remove old
      localStorage.removeItem(oldKey);
      console.log(`🗑️ Removed duplicate old preference: ${oldKey}`);
    }
  });

  console.log(`✅ Migration complete: ${migrated} preferences migrated`);
};

/**
 * Verifies HIPAA compliance by checking localStorage for PHI violations
 */
export const verifyNoPhiInLocalStorage = () => {
  console.log("🔍 Verifying HIPAA compliance for localStorage...");

  const allKeys = Object.keys(localStorage);
  const violations = [];
  const safe = [];

  allKeys.forEach((key) => {
    // Check if it's a UI preference (safe)
    if (key.startsWith("pref_")) {
      const prefKey = key.replace("pref_", "");
      if (ALLOWED_PREFERENCES[prefKey]) {
        safe.push(key);
      } else {
        violations.push(`❌ Unknown UI preference: ${key}`);
      }
    } else {
      // Check for dangerous patterns that might contain PHI
      const dangerousPatterns = [
        "user",
        "token",
        "email",
        "consumer",
        "patient",
        "auth",
        "session",
        "login",
        "phi",
        "medical",
        "counselor",
        "client",
        "name",
        "phone",
        "address",
      ];

      const isDangerous = dangerousPatterns.some((pattern) =>
        key.toLowerCase().includes(pattern)
      );

      if (isDangerous) {
        violations.push(`🚨 POTENTIAL PHI: ${key}`);
      } else {
        violations.push(`⚠️ Non-UI data: ${key}`);
      }
    }
  });

  console.log("✅ Safe UI preferences:", safe);

  if (violations.length > 0) {
    console.error("❌ Potential HIPAA violations found:", violations);
    console.error("🔧 Action needed: Remove or migrate these items");
    return false;
  } else {
    console.log("✅ HIPAA compliance verified - no violations found!");
    return true;
  }
};

/**
 * Cleans up non-UI localStorage items for HIPAA compliance
 * USE WITH CAUTION - This will remove all non-UI data from localStorage
 */
export const cleanupNonUILocalStorage = () => {
  console.log(
    "🧹 Cleaning up non-UI localStorage items for HIPAA compliance..."
  );

  const allKeys = Object.keys(localStorage);
  const toRemove = [];

  allKeys.forEach((key) => {
    // Only keep items that start with 'pref_' and are in the whitelist
    if (key.startsWith("pref_")) {
      const prefKey = key.replace("pref_", "");
      if (!ALLOWED_PREFERENCES[prefKey]) {
        toRemove.push(key);
      }
    } else {
      toRemove.push(key);
    }
  });

  toRemove.forEach((key) => {
    console.log(`🗑️ Removing non-UI data: ${key}`);
    localStorage.removeItem(key);
  });

  console.log(`✅ Cleaned up ${toRemove.length} items for HIPAA compliance`);

  // Verify compliance after cleanup
  const isCompliant = verifyNoPhiInLocalStorage();
  if (isCompliant) {
    console.log("🎉 localStorage is now HIPAA compliant!");
  }

  return toRemove.length;
};

/**
 * Safe initialization function - call this on app startup
 * This will migrate old preferences and verify HIPAA compliance
 */
export const initializeHIPAACompliantStorage = () => {
  console.log("🔐 Initializing HIPAA-compliant localStorage...");

  try {
    // Step 1: Migrate old UI preferences
    migrateOldUIPreferences();

    // Step 2: Verify compliance
    const isCompliant = verifyNoPhiInLocalStorage();

    if (!isCompliant) {
      console.warn("🚨 HIPAA compliance issues detected!");
      console.warn(
        "Consider running cleanupNonUILocalStorage() to fix automatically"
      );

      // Optionally auto-cleanup (uncomment if you want automatic cleanup)
      // console.log("🔧 Running automatic cleanup...");
      // cleanupNonUILocalStorage();
    }

    console.log("✅ HIPAA-compliant storage initialization complete");
    return isCompliant;
  } catch (error) {
    console.error("❌ Failed to initialize HIPAA-compliant storage:", error);
    return false;
  }
};

// Legacy support - keep the old export name for backward compatibility
// This allows existing code to continue working while you migrate
export const usePersistentState = (key, defaultValue) => {
  console.warn(
    `⚠️ usePersistentState("${key}") is deprecated. Use useUIPreference("ui-${key}") instead for HIPAA compliance.`
  );

  // Check if this is a UI preference that can be migrated
  const uiKey = `ui-${key}`;
  if (ALLOWED_PREFERENCES[uiKey]) {
    return useUIPreference(uiKey, defaultValue);
  } else {
    throw new Error(
      `❌ usePersistentState("${key}") is not allowed for HIPAA compliance. ` +
        `Use useUIPreference() with an allowed UI preference key instead.`
    );
  }
};
